import { Box, Center, HStack, VStack, Text, Image } from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { LuBot } from "react-icons/lu";
import { BaseBlockProps } from "./types";

interface ArtifactContent {
  id: string;
  title?: string;
  label?: string | null;
  description?: string;
  type?: string;
}

export const ArtifactBlock = ({ block }: BaseBlockProps) => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();

  // Extract content from the new block shape
  const content = block.content as ArtifactContent | undefined;
  const artifactId = content?.id;
  const title = content?.title || block.title || "Artifact";

  const handleClick = () => {
    if (artifactId && sessionId) {
      navigate(`/luna/${sessionId}/artifact/${artifactId}`);
    }
  };

  return (
    <Box
      as="button"
      onClick={handleClick}
      cursor={artifactId ? "pointer" : "default"}
      w="full"
      maxW="800px"
      textAlign="left"
      my="md"
      _hover={artifactId ? { transform: "translateY(-1px)" } : undefined}
      transition="transform 0.15s"
    >
      <Box
        borderRadius="component.md"
        bg="bg.app"
        boxShadow="0 0 1px 0 rgba(0, 0, 0, 0.32), 0 0 2px 0 rgba(0, 0, 0, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.08)"
        overflow="hidden"
      >
        <HStack spacing="md" p="sm" align="center">
          {/* Preview thumbnail */}
          <Box
            w="94px"
            h="72px"
            borderRadius="component.md"
            overflow="hidden"
            bg="bg.container"
            boxShadow="0 0 1px 0 rgba(0, 0, 0, 0.32), 0 0 2px 0 rgba(0, 0, 0, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.08)"
            flexShrink={0}
            display="flex"
            alignItems="flex-start"
            p="sm"
          >
            {block.thumbnail ? (
              <Image
                src={block.thumbnail as string}
                alt=""
                objectFit="cover"
                w="100%"
                h="100%"
              />
            ) : content?.type === "subagent" ? (
              <Center w="full" h="full">
                <LuBot size={28} color="var(--chakra-colors-p-500)" />
              </Center>
            ) : (
              // Placeholder report graphic - simple lines to represent a document
              <VStack spacing="xs" w="full" align="stretch">
                <Box h="2px" bg="p.300" w="60%" />
                <Box h="2px" bg="neutral.200" w="100%" />
                <Box h="2px" bg="neutral.200" w="100%" />
                <Box h="2px" bg="neutral.200" w="80%" />
                <Box h="2px" bg="p.200" w="40%" mt="xs" />
              </VStack>
            )}
          </Box>

          {/* Content */}
          <VStack align="start" spacing="sm" flex={1} py="xs">
            <Text
              fontWeight="semibold"
              color="text.body.primary"
              fontSize="md"
              noOfLines={1}
            >
              {title}
            </Text>
            <Text fontSize="xs" color="text.body.tertiary">
              {content?.label ? `Artifact • ${content.label}` : "Artifact"}
            </Text>
          </VStack>
        </HStack>
      </Box>
    </Box>
  );
};
